import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { ucFirst } from "@/utils";

interface StatusCardProps {
  title: string;
  icon: React.ReactNode;
  counts: Record<string, number>;
  isLoading: boolean;
  colorMap: Record<string, string>;
  description?: string;
}

export function StatusCard({ title, icon, counts, isLoading, colorMap, description }: StatusCardProps) {
  const totalCount = Object.values(counts).reduce((sum, count) => sum + count, 0);

  return (
    <Card className="bg-gradient-to-br from-card/50 to-card shadow-md hover:shadow-lg transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="text-primary/80">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold">{totalCount}</div>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
          
          {isLoading ? (
            <div className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading...
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(counts)
                .filter(([_, count]) => count > 0) // Only show statuses with values > 0
                .map(([status, count]) => (
                  <Badge
                    key={status}
                    variant="secondary"
                    className={`${colorMap[status] || 'bg-gray-100 text-gray-800'}`}
                  >
                    {ucFirst(status)}: {count}
                  </Badge>
                ))}
              {Object.entries(counts).filter(([, count]) => count > 0).length === 0 && (
                <span className="text-sm text-muted-foreground">No data available</span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}